import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Camera, ClipboardList, Package, Plus, Search, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { ReturnOrder, ReturnStatus, Yard } from '../../types/stock.types';
import { returnsApi, yardsApi } from '../../utils/stock-api';
import ReturnStatusPill from './components/ReturnStatusPill';

const ALL = 'all';

const STATUS_FILTERS: { value: string; label: string }[] = [
    { value: ALL, label: 'All statuses' },
    { value: 'open', label: 'Open' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'completed', label: 'Completed' },
    { value: 'ready_to_enter', label: 'Ready to Enter' },
    { value: 'entered', label: 'Entered' },
    { value: 'voided', label: 'Voided' },
];

/**
 * Return Orders list — STOCK.md §5, routed per yard (STOCK-MENU.md §2).
 *
 * Yard-scoped: the route carries :yardId, so this is "Sydney Yard → Returns", not a
 * global list with a filter. The yard picker only appears on the unscoped route, which
 * exists for the cross-yard view.
 *
 * Photo and line counts are shown per row because they are the two gates that decide
 * whether a return can be completed. Seeing "2/6 photos" in the list is what stops a
 * yardsman opening a return, finding it half-done and having to work out why.
 */
export default function ReturnOrdersPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_RETURN_ORDER);
    const navigate = useNavigate();
    const { yardId: yardIdParam } = useParams<{ yardId?: string }>();
    const scopedYardId = yardIdParam ? Number(yardIdParam) : null;

    const [orders, setOrders] = useState<ReturnOrder[]>([]);
    const [yards, setYards] = useState<Yard[]>([]);
    const [minPhotos, setMinPhotos] = useState(6);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<string>(ALL);
    const [yardFilter, setYardFilter] = useState<string>(ALL);
    const [search, setSearch] = useState('');

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            // The route's yard always wins over the picker — on a scoped route the picker
            // is not rendered, and a stale filter value must not leak across yards.
            const effectiveYard = scopedYardId
                ? String(scopedYardId)
                : (yardFilter === ALL ? undefined : yardFilter);

            setOrders(await returnsApi.list({
                status: status === ALL ? undefined : status,
                yardId: effectiveYard,
            }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load return orders');
        } finally {
            setLoading(false);
        }
    }, [status, yardFilter, scopedYardId]);

    useEffect(() => {
        if (canRead) fetchOrders();
    }, [canRead, fetchOrders]);

    useEffect(() => {
        if (!canRead) return;
        yardsApi.list({ status: 'open' }).then(setYards).catch(() => undefined);
        returnsApi.meta().then((m) => setMinPhotos(m.minPhotos)).catch(() => undefined);
    }, [canRead]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return orders;
        return orders.filter((o) =>
            [o.orderNumber, o.externalRef, o.project?.projectName, o.project?.projectNumber,
             o.assignedTo?.name, o.yard?.yardCode]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [orders, search]);

    if (!canRead) return <AccessRestricted />;

    /** Keeps the detail page inside the yard sub-tree when we are scoped to one. */
    const detailHref = (orderId: number) =>
        scopedYardId ? `/stock/yards/${scopedYardId}/returns/${orderId}` : `/stock/returns/${orderId}`;

    const scopedYard = scopedYardId ? yards.find((y) => y.id === scopedYardId) ?? null : null;

    const photoProgress = (o: ReturnOrder) => {
        const count = o.photoCount ?? 0;
        const done = count >= minPhotos;
        return (
            <span className={done ? 'text-emerald-700' : 'text-muted-foreground'}>
                {count}/{minPhotos}
            </span>
        );
    };

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Return Orders</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {scopedYard
                            ? `${scopedYard.yardName} · gear coming back from projects.`
                            : 'Gear coming back from projects into the yard.'}
                    </p>
                </div>
                <Can I={ACTIONS.CREATE} a={RESOURCES.STOCK_RETURN_ORDER}>
                    <Button
                        onClick={() => navigate(
                            scopedYardId ? `/stock/yards/${scopedYardId}/returns/new` : '/stock/returns/new'
                        )}
                        className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                    >
                        <Plus size={16} /><span>New Return Order</span>
                    </Button>
                </Can>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by order number, project or reference..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <div className="w-full md:w-[190px]">
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {STATUS_FILTERS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {/* Only on the cross-yard route — inside a yard the route is the filter. */}
                {!scopedYardId && (
                    <div className="w-full md:w-[190px]">
                        <Select value={yardFilter} onValueChange={setYardFilter}>
                            <SelectTrigger className="w-full bg-white">
                                <SelectValue placeholder="All yards" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All yards</SelectItem>
                                {yards.map((y) => (
                                    <SelectItem key={y.id} value={String(y.id)}>{y.yardCode}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                    {yards.length === 0 ? (
                        <>
                            <p>No return orders.</p>
                            <p className="text-sm mt-1">
                                Returns belong to a yard — create one under Stock → Yards first.
                            </p>
                        </>
                    ) : 'No return orders found'}
                </div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((o) => (
                            <Card
                                key={o.id}
                                className="p-4 active:bg-accent/40"
                                onClick={() => navigate(detailHref(o.id))}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <span className="font-mono font-medium">{o.orderNumber}</span>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">
                                            {o.project?.projectName ?? '–'}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            Job {o.project?.projectNumber ?? '–'}
                                        </p>
                                    </div>
                                    <ReturnStatusPill status={o.status} />
                                </div>
                                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1.5"><Camera size={14} />{photoProgress(o)}</span>
                                    <span className="flex items-center gap-1.5"><Package size={14} />{o.lineCount ?? 0} lines</span>
                                    <span className="flex items-center gap-1.5">
                                        <User size={14} />{o.assignedTo?.name ?? 'Unassigned'}
                                    </span>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Order Number</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Project</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Job Number</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Yard</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Return Date</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Photos</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Lines</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Assigned To</TableHead>
                                        <TableHead className="font-semibold text-slate-900">External Ref</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((o) => (
                                        <TableRow
                                            key={o.id}
                                            onClick={() => navigate(detailHref(o.id))}
                                            className="cursor-pointer"
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <ClipboardList size={16} className="text-muted-foreground" />
                                                    <span className="font-mono">{o.orderNumber}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="whitespace-normal max-w-[220px]">
                                                {o.project?.projectName ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell className="font-mono">
                                                {o.project?.projectNumber ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell className="font-mono text-muted-foreground">
                                                {o.yard?.yardCode ?? '–'}
                                            </TableCell>
                                            <TableCell>{o.returnDate ?? '–'}</TableCell>
                                            <TableCell>{photoProgress(o)}</TableCell>
                                            <TableCell>{o.lineCount ?? 0}</TableCell>
                                            <TableCell>
                                                {o.assignedTo?.name ?? <span className="text-muted-foreground">Unassigned</span>}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">
                                                {o.externalRef ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell><ReturnStatusPill status={o.status as ReturnStatus} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
